import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "../../Style/VATDeclarationPage.scss";
import "../../Style/VATTable.scss";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { errorAlert } from "../../Components/Alerts";
import CompanyHeader from "../FinancialStatementCRUD/FinancialS_Components/CompanyHeader";

export default function AnnualDeclarationPage() {
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);

    const [stokuTotal, setStokuTotal] = useState(0);
    const [txf60, setTxf60] = useState(0);
    const [txf62, setTxf62] = useState(0);
    const [txf67, setTxf67] = useState(0);
    const [txf68, setTxf68] = useState(0);
    const [txf70, setTxf70] = useState(0);
    const [txf75, setTxf75] = useState(0);
    const [txf76, setTxf76] = useState(0);
    const [txf61, setTxf61] = useState(0);
    const [txf62_2, setTxf62_2] = useState(0);



    const fetchCompaniesData = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
            if (response.data && response.data.length > 0) {
                setCompanyInfo(response.data[0]);
            }
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
        }
    };

    const fetchData = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "TaxReport/DeklarataVjetore", { headers, params: { fiscalYear } });
            const processedData = response.data.map(item => {
                Object.keys(item).forEach(key => { if (item[key] === null) item[key] = 0; });
                return item;
            });
            setTxf60(processedData[0]._60_TeArdhurat);
            setTxf62(processedData[0]._62_Malli);
            setTxf67(processedData[0]._67_Pagat);
            setTxf68(processedData[0]._68_ShpenzimetZhvleresimit);
            setTxf70(processedData[0]._70_ShpenzimetAdministrative);
            setTxf75(processedData[0]._75_TeArdhurat_Tjera);
            setTxf76(processedData[0]._76_ShpenzimetFinanciare);
        } catch (error) {
            if (error.response && error.response.status === 400) {
                // No access
            } else {
                errorAlert("Gabim në kërkesë:", error);
            }
        }
    };

    const fetchTotalAmount = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "AccountingBooks/TotaliStokut", { headers, params: { fiscalYear } });
            setStokuTotal(response.data[0].StokuTotal);
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
        }
    };

    const fetchVatDeclarationStockBalance = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "TaxReport/VatDeclarationStockBalance", { headers, params: { fiscalYear } });
            setTxf61(response.data[0].StokuNeFillim61);
            setTxf62_2(response.data[0].Blerjet62);
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
        }
    };

    const KMSH = Number(txf61) + Number(txf62_2) - Number(stokuTotal);
    const brutoFitimi = Number(txf60) - Number(KMSH);
    const shpenzimetOperative = Number(txf67) + Number(txf68) + Number(txf70);
    const fitimiAktivitetetOperative = Number(brutoFitimi) - Number(shpenzimetOperative);
    const aktivitetetJoOperative = Number(txf75) - Number(txf76);
    const teArdhuratNeto = Number(fitimiAktivitetetOperative) + Number(aktivitetetJoOperative);

    useEffect(() => {
        fetchCompaniesData();
        fetchData();
        fetchTotalAmount();
        fetchVatDeclarationStockBalance();
    }, [fiscalYear]);

    const fmt = (val) => Number(val || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const buildAnnualPdfDoc = () => {
        const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(companyInfo?.CompanyName || "Kompania", 105, 14, { align: "center" });
        doc.setFontSize(10);
        doc.text("Formulari i Deklarimit Vjetor dhe Pagesës për TAK", 105, 19, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(`Numri fiskal: ${companyInfo?.IdentificationNumber || "-"}`, 10, 23.5);
        doc.text(`Periudha: ${fiscalYear}`, 200, 23.5, { align: "right" });

        const sectionIndexes = new Set();
        const calcIndexes = new Set();
        const bodyRows = [];
        let ri = 0;

        const addSection = (title) => {
            bodyRows.push([{ content: title, colSpan: 3 }]);
            sectionIndexes.add(ri++);
        };
        const addRow = (code, desc, value) => {
            bodyRows.push([code, desc, Number(value || 0).toFixed(2)]);
            ri++;
        };
        const addCalc = (code, desc, value) => {
            bodyRows.push([code, desc, Number(value || 0).toFixed(2)]);
            calcIndexes.add(ri++);
        };

        addSection("Të ardhurat bruto lidhur me aktivitetet operative");
        addRow("[60]", "Të ardhurat bruto", txf60);

        addSection("Shpenzimet e lidhura me aktivitetet operative");
        addRow("[61]", "Stoku në fillim", txf61);
        addRow("[62]", "Blerjet apo kostoja e prodhimit", txf62_2);
        addCalc("[63]", "Total ([61]+[62])", Number(txf61) + Number(txf62_2));
        addRow("[64]", "Stoku në fund", stokuTotal);
        addCalc("[65]", "Kostoja e mallrave të shitura ([63]−[64])", KMSH);
        addCalc("[66]", "Bruto fitimi ([60]−[65])", brutoFitimi);
        addRow("[67]", "Pagat bruto", txf67);
        addRow("[68]", "Shpenzimet për zhvlerësim dhe amortizim (nuk përfshihen në koston e mallrave)", txf68);
        addRow("[69]", "Shpenzimet e shitjes", 0);
        addRow("[70]", "Shpenzimet e përgjithshme dhe administrative (nuk përfshihen në koston e mallrave)", txf70);
        addRow("[71]", "Shpenzimet për hulumtime dhe zhvillim", 0);
        addRow("[72]", "Shpenzimet tjera operative", 0);
        addCalc("[73]", "Totali i shpenzimeve operative ([67] deri [72])", shpenzimetOperative);
        addCalc("[74]", "Fitimi (humbja) nga aktivitetet operative ([66]−[73])", fitimiAktivitetetOperative);

        addSection("Aktivitetet jo-operative");
        addRow("[75]", "Të ardhurat tjera", txf75);
        addRow("[76]", "Shpenzimet tjera", txf76);
        addCalc("[77]", "Fitimi (humbja) nga aktivitetet jo-operative ([75]−[76])", aktivitetetJoOperative);
        addCalc("[78]", "Të ardhurat neto ose humbja ([74]+[77])", teArdhuratNeto);

        autoTable(doc, {
            startY: 34,
            head: [["Kodi", "Përshkrimi", "Shuma (€)"]],
            body: bodyRows,
            theme: "grid",
            styles: {
                fontSize: 7,
                cellPadding: 1,
                lineColor: [200, 200, 200],
                lineWidth: 0.1,
                textColor: [33, 33, 33],
            },
            headStyles: {
                fillColor: [40, 73, 69],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: {
                0: { cellWidth: 16, halign: "center" },
                1: { cellWidth: 140 },
                2: { cellWidth: 34, halign: "right" },
            },
            didParseCell: (hookData) => {
                if (sectionIndexes.has(hookData.row.index)) {
                    hookData.cell.styles.fillColor = [78, 123, 103];
                    hookData.cell.styles.fontStyle = "bold";
                    hookData.cell.styles.textColor = [255, 255, 255];
                }
                if (calcIndexes.has(hookData.row.index)) {
                    hookData.cell.styles.fillColor = [245, 249, 247];
                    hookData.cell.styles.fontStyle = "bold";
                }
            },
            margin: { top: 10, right: 6, bottom: 10, left: 6 },
            pageBreak: "auto",
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let page = 1; page <= totalPages; page++) {
            doc.setPage(page);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text(`Faqe ${page}/${totalPages}`, 6, 292);
            doc.text("Gjeneruar nga Accountree", 200, 292, { align: "right" });
        }

        return { doc };
    };

    const handleDownloadPdf = async () => {
        if (isGeneratingPdf || isGeneratingPrint) return;
        setIsGeneratingPdf(true);
        try {
            const { doc } = buildAnnualPdfDoc();
            doc.save(`Deklarata_Vjetore_TAK_${fiscalYear}.pdf`);
        } catch (error) {
            console.error("Gabim gjatë gjenerimit të PDF:", error);
            window.alert("Ndodhi gabim gjatë gjenerimit të PDF.");
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handlePrintPdf = async () => {
        if (isGeneratingPdf || isGeneratingPrint) return;
        setIsGeneratingPrint(true);
        try {
            const { doc } = buildAnnualPdfDoc();
            const blobUrl = doc.output("bloburl");
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(blobUrl);
                }, 1000);
            };
        } catch (error) {
            console.error("Gabim gjatë printimit:", error);
            window.alert("Ndodhi gabim gjatë printimit.");
        } finally {
            setIsGeneratingPrint(false);
        }
    };

    return (
        <div className="VATD__Page">
            <div className="VATD__HeaderData">
                <CompanyHeader
                    reportTitle="FORMULARI I DEKLARIMIT VJETOR DHE PAGESES PER TATIMIN NË TË ARDHURAT E KORPORATAVE"
                    reportSubtitle="Tatimi në të Ardhurat e Korporatave"
                    fiscalYear={fiscalYear}
                    onDownloadPdf={handleDownloadPdf}
                    isGeneratingPdf={isGeneratingPdf}
                    onPrint={handlePrintPdf}
                    isGeneratingPrint={isGeneratingPrint}
                />
            </div>

            <div className="VATD__TableSection">
                <div className="VATD__TableSection__Head">
                    <h3>Deklarata Vjetore TAK · {fiscalYear}</h3>
                </div>
                <div className="VATT__Wrapper">
                    <table className="VATT__Table">
                        <thead>
                            <tr className="VATT__ColHead">
                                <th className="VATT__Th--code">Kodi</th>
                                <th>Përshkrimi</th>
                                <th className="VATT__Th--num">Shuma (€)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* ── Section 1 ── */}
                            <tr className="VATT__SectionRow">
                                <td colSpan={3}>Të ardhurat bruto lidhur me aktivitetet operative</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[60]</td>
                                <td className="VATT__Td--desc">Të ardhurat bruto</td>
                                <td className="VATT__Td--num">{fmt(txf60)}</td>
                            </tr>

                            {/* ── Section 2 ── */}
                            <tr className="VATT__SectionRow">
                                <td colSpan={3}>Shpenzimet e lidhura me aktivitetet operative</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[61]</td>
                                <td className="VATT__Td--desc">Stoku në fillim</td>
                                <td className="VATT__Td--num">{fmt(txf61)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[62]</td>
                                <td className="VATT__Td--desc">Blerjet apo kostoja e prodhimit</td>
                                <td className="VATT__Td--num">{fmt(txf62_2)}</td>
                            </tr>
                            <tr className="VATT__SummaryRow">
                                <td className="VATT__Td--code">[63]</td>
                                <td className="VATT__Td--desc">Total ([61]+[62])</td>
                                <td className="VATT__Td--num">{fmt(Number(txf61) + Number(txf62_2))}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[64]</td>
                                <td className="VATT__Td--desc">Stoku në fund</td>
                                <td className="VATT__Td--num">{fmt(stokuTotal)}</td>
                            </tr>
                            <tr className="VATT__SummaryRow">
                                <td className="VATT__Td--code">[65]</td>
                                <td className="VATT__Td--desc">Kostoja e mallrave të shitura ([63]−[64])</td>
                                <td className="VATT__Td--num">{fmt(KMSH)}</td>
                            </tr>
                            <tr className="VATT__SummaryRow">
                                <td className="VATT__Td--code">[66]</td>
                                <td className="VATT__Td--desc">Bruto fitimi ([60]−[65])</td>
                                <td className="VATT__Td--num">{fmt(brutoFitimi)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[67]</td>
                                <td className="VATT__Td--desc">Pagat bruto</td>
                                <td className="VATT__Td--num">{fmt(txf67)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[68]</td>
                                <td className="VATT__Td--desc">Shpenzimet për zhvlerësim dhe amortizim (nuk përfshihen në koston e mallrave)</td>
                                <td className="VATT__Td--num">{fmt(txf68)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[69]</td>
                                <td className="VATT__Td--desc">Shpenzimet e shitjes</td>
                                <td className="VATT__Td--num">{(0).toFixed(2)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[70]</td>
                                <td className="VATT__Td--desc">Shpenzimet e përgjithshme dhe administrative (nuk përfshihen në koston e mallrave)</td>
                                <td className="VATT__Td--num">{fmt(txf70)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[71]</td>
                                <td className="VATT__Td--desc">Shpenzimet për hulumtime dhe zhvillim</td>
                                <td className="VATT__Td--num">{(0).toFixed(2)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[72]</td>
                                <td className="VATT__Td--desc">Shpenzimet tjera operative</td>
                                <td className="VATT__Td--num">{(0).toFixed(2)}</td>
                            </tr>
                            <tr className="VATT__SummaryRow">
                                <td className="VATT__Td--code">[73]</td>
                                <td className="VATT__Td--desc">Totali i shpenzimeve operative ([67] deri [72])</td>
                                <td className="VATT__Td--num">{fmt(shpenzimetOperative)}</td>
                            </tr>
                            <tr className="VATT__PayRow">
                                <td className="VATT__Td--code">[74]</td>
                                <td className="VATT__Td--desc">Fitimi (humbja) nga aktivitetet operative ([66]−[73])</td>
                                <td className="VATT__Td--num">{fmt(fitimiAktivitetetOperative)}</td>
                            </tr>

                            {/* ── Section 3 ── */}
                            <tr className="VATT__SectionRow">
                                <td colSpan={3}>Aktivitetet jo-operative</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[75]</td>
                                <td className="VATT__Td--desc">Të ardhurat tjera</td>
                                <td className="VATT__Td--num">{fmt(txf75)}</td>
                            </tr>
                            <tr className="VATT__Row">
                                <td className="VATT__Td--code">[76]</td>
                                <td className="VATT__Td--desc">Shpenzimet tjera</td>
                                <td className="VATT__Td--num">{fmt(txf76)}</td>
                            </tr>
                            <tr className="VATT__SummaryRow">
                                <td className="VATT__Td--code">[77]</td>
                                <td className="VATT__Td--desc">Fitimi (humbja) nga aktivitetet jo-operative ([75]−[76])</td>
                                <td className="VATT__Td--num">{fmt(aktivitetetJoOperative)}</td>
                            </tr>
                            <tr className="VATT__PayRow">
                                <td className="VATT__Td--code">[78]</td>
                                <td className="VATT__Td--desc">Të ardhurat neto ose humbja ([74]+[77])</td>
                                <td className="VATT__Td--num">{fmt(teArdhuratNeto)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
